import * as React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Skeleton } from '@ministry/ui/internal';

import { useSession } from '../lib/session.js';
import { hadSession, markHadSession } from '../lib/had-session.js';
import { SessionExpiredPage } from '../routes/SessionExpiredPage.js';
import { useT } from '../i18n/strings.js';

/**
 * Route guard for every authenticated screen. `AUTH_REQUIRED`/`SESSION_EXPIRED` both mean
 * "no usable session" here — but SCREEN_SPECS.md I24 distinguishes WHY that's true from
 * the user's point of view (`lib/had-session.ts`'s own comment): a session that lapsed
 * mid-task renders `SessionExpiredPage` (an explained, deliberate screen) rather than the
 * bare `<Navigate>` a first-ever, never-authenticated visit still correctly uses.
 */
export function RequireAuth(): React.JSX.Element {
  const { data, isLoading, isError } = useSession();
  const location = useLocation();
  const t = useT();

  React.useEffect(() => {
    if (data) markHadSession();
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6">
        <span role="status" aria-live="polite" className="sr-only">
          {t('shell.sessionLoading')}
        </span>
        <Skeleton shape="block" className="h-8 w-48" />
        <Skeleton shape="list-row" count={2} className="w-64" />
      </div>
    );
  }

  if (isError || !data) {
    if (hadSession()) return <SessionExpiredPage />;
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}
